/**
 * Demo brand resolution.
 *
 * The demoer can rebrand the demo tenant on the fly via Settings → Demo
 * Brand (POST /catalyst/demo/brand). UI strings that should reflect the
 * configured brand read it from here instead of hardcoding "Vitalora",
 * so flipping the demo to "Nintendo" before a customer meeting is a
 * single API call away.
 *
 * Resolution rules:
 *   1. Read the active enterprise from the same react-query cache the
 *      enterprise switcher uses (`useEnterprises()` → `["enterprises"]`).
 *      The enterprise list endpoint already returns `name`, so no extra
 *      fetch is needed.
 *   2. Use the live `enterprise.name` when the user is on the demo
 *      tenant. This makes the rebrand take effect on the next page
 *      navigation that re-reads from cache.
 *   3. Fall back to the seeded canonical name ("Vitalora") in two cases:
 *      a. enterprise list isn't hydrated yet (first paint),
 *      b. the user is not on the demo tenant — these strings only
 *         render on demo-gated routes anyway, so the fallback is dead
 *         code in practice but cheap insurance.
 *
 * The returned brand is a single string — the only field that changes.
 * Slugs, asset paths, and route names stay "vitalora" forever (see
 * audit notes in PR description). Seeded text content (Slack messages,
 * PDF document text, knowledge file titles) is NOT covered here: those
 * remain "Vitalora" verbatim and are documented as a known limitation.
 */
import { type Enterprise } from "@mg/schemas/src/commons";

import { useEnterprises } from "./queries/enterprises";
import { queryClient } from "./queryClient";
import { store } from "./store";

/** The seeded display name. Used as the fallback / reset target. */
export const DEFAULT_DEMO_BRAND_NAME = "Vitalora";

/**
 * Synchronous brand resolver. Safe to call outside React (e.g. in
 * activity-feed seed strings) — never throws, always returns a non-empty
 * string. Reads from the react-query cache that the auth-layout primes
 * in `beforeLoad`, so on a hydrated page this is the live name.
 */
export function getDemoBrandName(): string {
  const { value: user } = store.getState().auth;
  if (!user?.enterpriseId) return DEFAULT_DEMO_BRAND_NAME;
  const enterprises = queryClient.getQueryData<Enterprise[]>(["enterprises"]);
  if (!enterprises) return DEFAULT_DEMO_BRAND_NAME;
  const current = enterprises.find((e) => e._id === user.enterpriseId);
  if (current?.isDemo === true && current.name) return current.name;
  return DEFAULT_DEMO_BRAND_NAME;
}

/**
 * Hook variant for components. Re-renders when the enterprise list
 * query invalidates (which the rebrand mutation triggers).
 *
 * NB: returns a string, not `string | undefined`. Components don't have
 * to handle the loading state — the fallback covers it.
 */
export function useDemoBrandName(): string {
  const enterprises = useEnterprises();
  const { value: user } = store.getState().auth;
  if (!user?.enterpriseId) return DEFAULT_DEMO_BRAND_NAME;
  const current = enterprises.data?.find((e) => e._id === user.enterpriseId);
  if (current?.isDemo === true && current.name) return current.name;
  return DEFAULT_DEMO_BRAND_NAME;
}
